<template>
  <div class="video-player-container uk-relative uk-border-rounded" :class="'video-player-container-' + id" ref="observe">
    <div v-show="ready">
      <video 
        :id="'video-player-' + id" 
        ref="videoPlayer"
        class="video-js" 
        :playsinline="playsInline" 
        :webkit-playsinline="playsInline" 
        style="width: 100%">
      </video>
    </div>
    <div class="poster uk-relative" v-show="!ready">    
      <img :src="options.poster" class="uk-width-1-1">
      <div class="uk-position-center" v-show="!ready">
        <loading-indicator></loading-indicator>
      </div>      
    </div>
  </div>
</template>

<script>
import videojs from 'video.js'
import 'video.js/dist/video-js.css'

// Contrib ads
import 'videojs-contrib-ads'
import 'videojs-contrib-ads/dist/videojs-contrib-ads.css'

// Ima
import 'videojs-ima'
import 'videojs-ima/dist/videojs.ima.css'

// Other
import LoadingIndicator from './LoadingIndicator.vue'
import Cookies from 'js-cookie';
import { installVideoFallback } from '../js/videoFallback';

export default {
  name: 'VideoPlayer',
  components: { LoadingIndicator },
  props: {
		id: {
			default: Math.floor(Math.random() * 10000)
		},
    ads: {
      type: Boolean,
      default: true
    },
		adsVdur: { // max time of ad
			type: Number,
			default: 60,
		},			
    playsInline: {
     type: Boolean,
     default: false         
    },
    isEmbeddable: {
     type: Boolean,
     default: false     
    },
    embedCode: {
      type: String,
      default: `<iframe src="https://www.site.com/video/id" width="560" height="315" frameborder="0"></iframe>`,
    },
    options: {
      type: Object,
      default() {
        return {};
      }
    },
    viewportObserver: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  data() {
    return {
      observer: null,
      player: null,
      shareModal: null,
      ready: false,
      isMuted: null,
      isPaused: null,
      consentInterval: null,
      consentAttempts: 0,      
			defaultOptions: {
				autoplay: false,
        aspectRatio: '16:9',
				fluid: true,
				controls: true,
        experimentalSvgIcons: true,
        poster: 'https://placehold.co/800x450?text=video',
				techOrder: [ 'html5' ]
			}
    }
  },
  mounted() {
    
    var app = this.$root;
    var component = this;
    var device = this.$root.device;

    if(component.ads) {
      component.startConsentCheck();
    } else {
      component.init();
    }
  },
  beforeUnmount() {
    if (this.player) {
      this.player.dispose();
    }
    if(this.observer) {
      this.observer.disconnect();
    }
  },
	methods: {
  
    init: function() {

      var component = this;

      // Attach observer
      if(component.viewportObserver == true) { 
        component.initObserver();
      }
      
      // Set up the VAST options
      //var baseVastUrl = 'https://www.nieuwnieuws.nl/feeds/vast.xml';
      var baseVastUrl = 'https://7b936.v.fwmrm.net/ad/g/1?prof=506166:sanoma_sbs_ovp_js_live&nw=506166&caid=nieuwnieuws_inventory&vdur=50&vprn=61609119761261196&csid=nieuwnieuws_desktop&flag=+sltp+emcr+exvt+fbad&pvrn=64478784911208316&resp=vast4;talpa_consent=0&_fw_gdpr=1&_fw_gdpr_consent=%_FW_GDPR_CONSENT%;';
  
			// Different URL for devMode
			if(component.$helper.getURLParameter('waterfall') == 'true') {
				baseVastUrl = "https://www.nieuwnieuws.nl/feeds/vast.xml";		
			}
			
      var finalAdUrl = component.updateAdUrl(baseVastUrl);
          
      var imaOptions = {
        adTagUrl: finalAdUrl,
        disableAdControls: false,
        debug: true
      };
        
      // Merged options
      const mergedOptions = Object.assign({}, component.defaultOptions, component.options);
      
      component.$logger('app/components/video-player/init')
            
      // Create player instance
      component.player = videojs(component.$refs.videoPlayer, mergedOptions, () => {
        component.$logger('app/components/video-player/ready')
        component.ready = true;
      });
  
      installVideoFallback(component.player);

      // Add event listeners
      component.player.on('pause', component.handlePause);
      component.player.on('volumechange', component.handleVolumeChange);
      component.player.on('play', component.handlePlay);
      component.player.on('pause', component.handlePause);
            
      // Initialize the VAST plugin
      if(component.ads && typeof google != undefined) {
				
        component.$logger('app/components/video-player/ads-enabled');
				
				// Ad event: adsready
				component.player.on("adsready", function (e) {
					
					if(typeof component.player.ima == "undefined") {
						return;
					}
											
					component.$logger('app/components/video-player/' + component.id + '/ads/ready');
								
					component.player.ima.addEventListener(google.ima.AdEvent.Type.LOADED, function(event) {
							var ad = event.getAd();						
							var adSystem = ad.getAdSystem();
							var clickThroughUrl = ad.data.clickThroughUrl;
							
							component.$logger('app/components/video-player/' + component.id + '/ads/loaded/adSystem/' + adSystem);
							component.$logger('app/components/video-player/' + component.id + '/ads/loaded/clickUrl/' + clickThroughUrl);
														
							component.$logger(JSON.stringify(ad));
					});
										
					// Ad event: started
					component.player.ima.addEventListener(google.ima.AdEvent.Type.STARTED, function (event) {
						component.$logger('app/components/video-player/' + component.id + '/ads/started');
					});	
					
					// Ad event: ended/completed
					component.player.ima.addEventListener(google.ima.AdEvent.Type.COMPLETE, function () {
						component.$logger('app/components/video-player/' + component.id + '/ads/completed');
					});	
				});
								
        component.player.ima(imaOptions);       
        component.player.ima.initializeAdDisplayContainer(); // Required for mobile
      } 
			
			// Ad event: adsError
			component.player.on("adserror", function (e) {
				component.$logger('app/components/video-player/' + component.id + '/ads/error');
				console.error(e.data.AdError.data)							 	
      });
			
      // Add embed button
      if(component.isEmbeddable) {
        component.addEmbedButton();
      }
    }, 
    initObserver: function() {
      
      var component = this;
      
      const options = {
        root: null,
        rootMargin: '-30% 0% -30% 0%',
        threshold: 0.5
      };
      
      var elementToObserve = this.$refs.observe;
      
      component.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) { 
            if(!window.userInteracted ||               
              Cookies.get('videoPlayerMuted') == 'true'
            ) {
              component.player.muted(true);
            } else {
              component.player.muted(false);
            }
            component.player.play();
          } else {
            component.player.pause();       
          }
        });
      }, options);
    
      component.observer.observe(elementToObserve); 
    },
    copyToClipboard: function(string) {
    },
    handleVolumeChange: function() {
      this.isMuted = this.player.muted();
      // Store in cookie so we can check it on other videoplayer in the page
      Cookies.set('videoPlayerMuted', this.isMuted, { expires: 365 });
    },   
    handlePause: function() {
      this.isPaused = true;
    },
    handlePlay: function() {
      this.isPaused = false;
    },      
    addEmbedButton: function() {

      var component = this;
      
      // Create Button 
      const myButton = component.player.getChild('ControlBar').addChild('button', {
        controlText: 'Delen',
        className: 'vjs-visible-text',
        clickHandler: function(event) {  
          
          // Open Modal        
          component.shareModal.open();
          
          // Attach click listener so we can copy the code to clipboard
          var embedButton = document.getElementById('video-embed-button-' + component.id);
          var embedCode = document.getElementById('video-embed-code-' + component.id);
          
          embedButton.addEventListener('click', () => {
            embedCode.select();
            document.execCommand('copy');
          });
        }
      });
      
      // Set icon
      myButton.setIcon('share');
      
      // Create Modal overlay
      const ModalDialog = videojs.getComponent('ModalDialog');
     
      var modalHtmlContent = document.createElement('div');
      modalHtmlContent.innerHTML = `
        <div class="vjs-embed-modal uk-padding">
          <div class="uk-position-center">
            <h4>Video embedden</h4>
            <div class="uk-grid uk-grid-small">
              <div class="uk-width-3-4">
                <input type="text" class="uk-input uk-width-1-1" id="video-embed-code-${component.id}" value='${component.embedCode}'>
              </div>
              <div class="uk-width-expand">
                <a class="uk-button uk-button-primary" id="video-embed-button-${component.id}">kopiëren</a>
              </div>
            </div>
          </div>
        </div>
      `;
    
      component.shareModal = new ModalDialog(component.player, {
        temporary: false,
        content: modalHtmlContent
      });
            
      component.player.addChild(component.shareModal);
           
    },    
    updateAdUrl: function(url) {
    
			var app = this.$root;
			var device = app.device;
			var component = this;
			var adUrl = url;
			
			if(!url) {
				return;
			}
			
		 	// Set default parameters
		 	var csid = 'nieuwnieuws_desktop';
		 	var consentString = app.consent;
		 	//var talpaConsent = (consentString) ? '1' : '0';
		 	var pvrn = Math.floor(Math.random() * 100000000);
		 	var vprn = Math.floor(Math.random() * 1000000000)
		 	var vdur = component.adsVdur;
					 	
		 	// Update csid based on platform type
		 	if(device.app) {
			 	csid = (device.android) ? 'nieuwnieuws_inapp' : 'nieuwnieuws_inapp';
		 	} else if(device.mobile) {
			 	csid = (device.android) ? 'nieuwnieuws_mweb' : 'nieuwnieuws_mweb';
		 	}	
					
			// Update query params 
			adUrl = component.$helper.updateQueryStringParameter(adUrl, 'csid', csid);
			adUrl = component.$helper.updateQueryStringParameter(adUrl, 'pvrn', pvrn);
			adUrl = component.$helper.updateQueryStringParameter(adUrl, 'vprn', vprn);
			adUrl = component.$helper.updateQueryStringParameter(adUrl, 'vdur', vdur);

			// Replace with consent			
			adUrl = adUrl.replace('%_FW_GDPR_CONSENT%', consentString);
			
			// Custom VAST waterfall, add consent, provider
			if(component.$helper.getURLParameter('waterfall') == 'true') {
				adUrl = component.$helper.updateQueryStringParameter(adUrl, 'consent', consentString);
				
				var provider = component.$helper.getURLParameter('provider')
				adUrl = component.$helper.updateQueryStringParameter(adUrl, 'provider', provider);
			}
			
			component.$logger('app/components/video-player/' + component.id + '/update-ad-url: ' + adUrl);
			
			return adUrl;
    },
    startConsentCheck: function() {
      
      var app = this.$root;
      var component = this;
      var maxChecks = (component.isEmbeddable) ? 2 : 6;
      
     component.$logger('app/components/video-player/start-consent-check')
     
      // Set up polling every second
      component.consentInterval = setInterval(() => {
        
        component.consentAttempts++;
        
        if (app.consent && app.consent.length > 100) {
          
          // Init player
          component.init();
      
          // Clear interval
          component.clearConsentInterval();
          
        } else if (component.consentAttempts >= maxChecks) {
          
          // Give up consent, init player
          component.init();
          
          // Clear interval
          component.clearConsentInterval();
          
          // reset attempts		
          component.consentAttempts = 0;		
        }
      }, 500);
          
    },
    clearConsentInterval: function() {
      var component = this;
      clearInterval(component.consentInterval);
      component.consentInterval = null;
    },
	}
}
</script>

<style scoped>

</style>
